import { Component, OnInit, ElementRef, OnDestroy, ViewChild, effect, ChangeDetectorRef, Inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { filter, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { RestaurantsService } from '../../services/restaurant.service';
import { Restaurant } from 'src/interfaces/restaurant.model';
import { FullscreenService } from 'src/app/services/fullscreen.service';
import { DataService } from 'src/app/services/data.service';
import { LocaleService } from 'src/app/services/locale.service';
import { Locale } from 'src/interfaces/locale.model';
import { StoplistService } from 'src/app/services/stoplist.service';
import { Analytics, logEvent } from 'firebase/analytics';
import { ANALYTICS } from 'src/app/firebase.providers';
import { v4 as uuidv4 } from 'uuid';
import { BasketService } from 'src/app/services/basket.service';
import { LocalizationService } from 'src/app/services/localization.service';
import { ScrollService } from 'src/app/services/scroll.service';
import { MenuSearchService } from 'src/app/services/menu-search.service';
import { MatDialog } from '@angular/material/dialog';
import { Table } from 'src/interfaces/table.model';
import { WaiterCallService } from 'src/app/services/waiter-call.service';
import { LinkService, isStoreId } from 'src/app/services/link.service';
import { DESKTOP_QUERY } from 'src/app/desktop/desktop-menu.model';
import { MENU_LANGUAGE_KEY } from 'src/app/desktop/desktop-menu/desktop-menu.component';

@Component({
    selector: 'app-menu-homepage',
    templateUrl: './menu-homepage.component.html',
    styleUrls: ['./menu-homepage.component.css'],
    standalone: false
})

export class MenuHomepageComponent implements OnInit, OnDestroy {
  constructor(
    private restaurantsService: RestaurantsService,
    private localeService: LocaleService,
    private stoplistService: StoplistService,
    public dataService: DataService,
    public basketService: BasketService,
    private scrollService: ScrollService,
    private dialog: MatDialog,
    private route: ActivatedRoute,
    private router: Router,
    private fullscreenService: FullscreenService,
    private languageService: LocalizationService,
    private elementRef: ElementRef,
    @Inject(ANALYTICS) private analytics: Analytics,
    private cdr: ChangeDetectorRef,
    private waiterCallService: WaiterCallService,
    private linkService: LinkService,
    public menuSearch: MenuSearchService
  ) {
    // Загрузка или генерация guestId
    this.guestId = localStorage.getItem('guest_id') || uuidv4();

    // Реакция на сигнал isListRendered
    effect(() => {
      const isRendered = this.dataService.isListRendered();
      console.log(`MenuHomepageComponent | effect | isRendered: ${isRendered}`);
      if (isRendered) {
        this.setupIntersectionObserver();
      }
    });
  }

  private destroy$ = new Subject<void>();
  private restaurantId = '';
  private demoTable: Table | null = null;
  private tableId: string | null = null;
  private guestId: string;
  private demo = false;

  /**
   * Десктоп-меню — самостоятельная вёрстка для просмотра: только по ссылке
   * заведения (slug или id, без стола) и на широком экране. Стол (`/T/<код>`,
   * старый QR) — всегда мобильная вёрстка с заказом, на любом экране
   */
  desktop = false;
  private storeLink = false;
  private wide = window.matchMedia(DESKTOP_QUERY);
  private onWideChange = () => this.updateLayout();

  @ViewChild('container') container!: ElementRef;
  @ViewChild('sentinel') sentinel!: ElementRef;

  ngOnInit(): void {
    // this.fullscreenService.openFullscreen();
    // this.openFullscreen();
    void this.open();
  }

  /**
   * Адрес → заведение и стол. Сначала новые адреса (одно чтение), затем
   * старые — как раньше:
   * - `/T/<код>` — стол по tableCodes;
   * - `/<сегмент>` — заведение по storeSlugs (в том числе `/demo`); не
   *   найден, но похож на UUID — id заведения (старый адрес);
   * - `/<заведение>/<qrCode>` — старый адрес QR, без изменений
   */
  private async open(): Promise<void> {
    const params = this.route.snapshot.paramMap;
    let restaurantId = params.get('restaurantId');
    let tableId = params.get('tableId');
    const code = params.get('code');
    this.storeLink = code === null && tableId === null;
    this.updateLayout();
    this.wide.addEventListener('change', this.onWideChange);

    if (code !== null) {
      const table = await this.linkService.resolveTableCode(code);
      restaurantId = table?.store ?? null;
      tableId = table?.qrCode ?? null;
    } else if (restaurantId !== null && tableId === null) {
      const store = await this.linkService.resolveSlug(restaurantId);
      restaurantId = store ?? (isStoreId(restaurantId) ? restaurantId : null);
    }
    this.start(restaurantId, tableId);
  }

  private updateLayout(): void {
    this.desktop = this.storeLink && this.wide.matches;
  }

  private start(restaurantId: string | null, tableId: string | null): void {
    if (restaurantId === null) {
      // Редирект на другую страницу, если restaurant == null
      // console.log('MenuHomepageComponent | ngOnInit() | restaurant == null');
      window.location.href = 'https://edim.online';
      return;
    }

    // Analytics
    logEvent(this.analytics, 'page_view', { page: 'Home', restaurant_id: restaurantId });

    // Save or update guestId
    localStorage.setItem('guest_id', this.guestId);
    console.log('MenuHomepageComponent | ngOnInit() | guestId:', this.guestId);

    this.restaurantId = restaurantId;
    this.tableId = tableId;

    this.restaurantsService.subscribeToRestaurant(this.restaurantId); // Запускаем подписку

    // Подписываемся на изменения ресторана
    let first = true;
    this.restaurantsService.restaurant$
      .pipe(
        takeUntil(this.destroy$),
        filter((data): data is Restaurant => data !== null), // Фильтруем null-значения
      )
      .subscribe((data: Restaurant) => {
        // Демо-заведение — по полю stores.demo («Три метлы», edim.menu/demo)
        this.demo = data.demo === true;
        // Промо-заведение (ROADMAP 5a) — только просмотр: стола нет, даже
        // если он есть в ссылке, — как ссылка заведения
        const promo = data.promo === true;
        if (promo) {
          this.tableId = null;
          this.storeLink = true;
          this.updateLayout();
        }
        if (first) {
          first = false;
          this.prepareSession(data);
        }
        this.dataService.restaurant = data;
        const table = data.tables.find((table) => table.id === this.tableId);
        this.dataService.restaurant.demo = this.demo;
        this.dataService.setTable(promo ? null : table || this.demoTable);

        this.dataService.setTableId(this.tableId); // Устанавливаем tableId
        this.dataService.guestId = this.guestId;

        // Загружаем стоп-лист для ресторана
        this.stoplistService.init(this.restaurantId);

        // Запускаем подписку на локализацию
        this.localeService.subscribeToLocale(data.locale);
      });

    // Подписываемся на изменения локализации
    this.localeService.locale$
      .pipe(
        takeUntil(this.destroy$),
        filter((data): data is Locale => data !== null), // Фильтруем null-значения
      ).subscribe((data: Locale) => {
        this.dataService.locale = data;
        // Устанавливаем язык страницы
        this.setPageLanguage(data, this.demo);
      });
  }

  /**
   * Стол и корзина — при первом получении заведения (демо известно только
   * из документа). Демо-заведение без стола в адресе — виртуальный
   * демо-стол, id которого живёт в localStorage (корзина между визитами).
   * Ключ свой (`demo_table_id`), чтобы стол другого заведения не стал
   * демо-столом
   */
  private prepareSession(data: Restaurant): void {
    if (this.demo && !data.tables.some((table) => table.id === this.tableId)) {
      this.tableId = localStorage.getItem('demo_table_id') || uuidv4();
      localStorage.setItem('demo_table_id', this.tableId);
      this.demoTable = new Table({
        id: this.tableId,
        description: 'Любимый столик Гарри Поттера',
        // Официанта демо-заказа ставит функция (сотрудник демо-заведения)
        waiters: [],
        waiterCall: true
      });
    }

    const storedRestaurant = localStorage.getItem('restaurant_id');
    const storedTable = localStorage.getItem('table_id');
    if (storedRestaurant != this.restaurantId || storedTable != this.tableId) {
      localStorage.setItem('restaurant_id', this.restaurantId);
      this.basketService.clearBasket();
    }
    if (this.tableId) {
      localStorage.setItem('table_id', this.tableId);
    }
    console.log('MenuHomepageComponent | prepareSession() | demo:', this.demo, 'restaurantId:', this.restaurantId, 'tableId:', this.tableId);
  }

  // onFooterRendered(): void {
  //   console.log('MenuHomepageComponent | onFooterRendered | Footer is ready, setting up observer');
  //   this.cdr.detectChanges(); // Принудительно обновляем DOM
  //   this.setupIntersectionObserver();
  // }


  private setupIntersectionObserver(): void {
    if (!this.sentinel?.nativeElement) {
      console.warn('MenuHomepageComponent | setupIntersectionObserver | Sentinel element not found');
      return;
    }

    console.log('MenuHomepageComponent | setupIntersectionObserver | Setting up observer for sentinel:', this.sentinel.nativeElement);
    const observer = new IntersectionObserver(
      ([entry]) => {
        console.log('MenuHomepageComponent | IntersectionObserver | isIntersecting:', entry.isIntersecting, 'boundingClientRect:', entry.boundingClientRect, 'rootBounds:', entry.rootBounds);
        this.waiterCallService.setShowWaiterCall(entry.isIntersecting);
      },
      {
        root: null, // Наблюдаем относительно вьюпорта
        threshold: 0.1, // 50% элемента должно быть видно
        rootMargin: '0px 0px -50px 0px' // Сдвиг области видимости, высота footer
      }
    );

    observer.observe(this.sentinel.nativeElement);
  }


  private setPageLanguage(locale: Locale, demo: boolean = false) {
    const browserLang = navigator.language || navigator.languages[0];
    let selectedLang = locale.code;

    console.log(`Browser language: ${browserLang}`);

    // Язык, выбранный гостем в подвале десктоп-меню, — если он есть у меню
    let stored: string | null = null;
    try {
      stored = localStorage.getItem(MENU_LANGUAGE_KEY);
    } catch {
      stored = null;
    }
    if (stored && (stored === locale.code || locale.languages?.includes(stored))) {
      selectedLang = stored;
      // Если язык браузера совпадает с языком ресторана — он и остаётся. Язык
      // страницы ставится и тогда: иначе подписи меню — по lang="ru" из
      // index.html (сербский гость видел «ХИТ», «Добавить»)
    } else if (browserLang.startsWith(selectedLang)) {
      console.log(`Browser language "${browserLang}" matched locale "${locale.code}"`);
    } else if (Array.isArray(locale.languages) && locale.languages.length > 0) {
      const matched = locale.languages.find(lang => browserLang.startsWith(lang));
      if (matched) {
        selectedLang = matched;
        console.log(`Browser language "${browserLang}" matched supported language "${matched}"`);
      } else if (locale.languages.includes('en')) {
        selectedLang = 'en';
        console.log(`No supported languages specified, using default locale: "en"`);
      } else {
        console.log(`No found supported languages specified, using default locale: "${locale.code}"`);
      }
    } else {
      console.log(`No supported languages specified, using current locale: "${locale.code}"`);
    }

    // Устанавливаем язык страницы
    document.documentElement.lang = selectedLang;
    this.dataService.language = selectedLang;
    console.log(`Language set to: ${selectedLang}`);

    // Analytics & update locale fields (time and units)
    // if (selectedLang !== locale.code) {
    // logEvent(this.analytics, 'language_change', { from: locale.code, to: selectedLang });
    // Подписываемся на локализацию для нового языка
    // this.localeService.subscribeToLocale(selectedLang);
    // this.subscription.add(
    //   this.localeService.locale$
    //     .pipe(
    //       filter((data): data is Locale => data !== null), // Фильтруем null-значения
    //       take(1), // Берем только первое значение, чтобы не держать подписку
    //       tap((partialLocale: Locale) => {
    //         // Обновляем локальные поля
    //         locale.currency = demo ? partialLocale.currency : locale.currency;
    //         locale.time = partialLocale.time;
    //         locale.units = partialLocale.units;
    //       })
    //     )
    //     .subscribe()
    // );
    // }
  }

  ngOnDestroy(): void {
    this.wide.removeEventListener('change', this.onWideChange);
    this.destroy$.next();
    this.destroy$.complete();
    // this.subscription.unsubscribe();
  }

  openFullscreen() {
    const elem = this.elementRef.nativeElement;

    if (elem.requestFullscreen) {
      elem.requestFullscreen();
    } else if (elem.mozRequestFullScreen) { /* Firefox */
      elem.mozRequestFullScreen();
    } else if (elem.webkitRequestFullscreen) { /* Chrome, Safari & Opera */
      elem.webkitRequestFullscreen();
    } else if (elem.msRequestFullscreen) { /* IE/Edge */
      elem.msRequestFullscreen();
    }
  }
}
